import React, { useCallback, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions, Plugin } from 'chart.js';
import { useParams } from 'react-router-dom';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';
import { durationText } from 'reusable/durationText';
import { businessRangeQuery, businessZoneQuery, toBusinessDate } from 'config/time';
import { CHART_SINGLE } from 'config/chartColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Define a plugin to adjust bar heights
const minHeightPlugin: Plugin<'bar'> = {
  id: 'minHeight',
  afterDatasetDraw: (chart, args, options) => {
    const { ctx, chartArea: { bottom }, scales: { y } } = chart;
    chart.getDatasetMeta(0).data.forEach((bar: any, index: number) => {
      const value = chart.data.datasets[0].data[index] as number;
      if (value > 0 && value < 6) {
        const minBarHeight = 5; // Minimum bar height in pixels
        const barHeight = Math.max(minBarHeight, y.getPixelForValue(value) - y.getPixelForValue(0));
        ctx.fillStyle = bar.options.backgroundColor as string;
        const yPos = bottom - barHeight;
        ctx.fillRect(bar.x - bar.width / 2, yPos, bar.width, barHeight);
      }
    });
  }
};

// Register the plugin
ChartJS.register(minHeightPlugin);

const WorkPerformanceGraph: React.FC = () => {
  const [monthlySummary, setMonthlySummary] = useState<any[]>([]);
  const beginRequest = useLatestRequest();
  const [result, setResult] = useState<{ key: string; error?: unknown } | null>(null);
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('driver');

  // ADM-05。年は日本時間の業務日で決め、範囲も業務日（1/1 0時〜12/31 23:59:59.999 JST）で送る。
  // 日付だけで送ると 1/1 の0〜9時と 12/31 の9時以降が外れていた。
  const currentYear = toBusinessDate(new Date()).slice(0, 4);

  const requestKey = `${id}-${currentYear}`;
  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    setResult(null);
    try {
      const yearRange = businessRangeQuery(
        'check_in',
        `${currentYear}-01-01`,
        `${currentYear}-12-31`
      );
      // RV-278・RV-279。サーバーの月の分け方を、画面の境界の計算と同じ時間帯にそろえる（いつも渡す）。
      const apiUrl = `/v1/attendances/summarize_monthly/?${yearRange}${businessZoneQuery()}user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      setMonthlySummary(response.data);
      setResult({ key: requestKey });
    } catch (error) {
      if (!isCurrent()) return;
      setResult({ key: requestKey, error });
    }
  }, [id, currentYear, requestKey, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const monthlyData = Array(12).fill(0);
  monthlySummary.forEach((summary) => {
    // EV-116。サーバーの月は会社の時間帯の月の `YYYY-MM`（段階2。以前は「日本時間の月初の0時を、
    // UTC の時刻の形にした値」）。どちらの形も先頭7文字が月なので、文字列の月を読む
    // （端末の時間帯の Date にすると、日本より西の端末で前の月になっていた）。
    const month = Number(String(summary.month).slice(5, 7)) - 1;
    if (!(month >= 0 && month < 12)) return;
    monthlyData[month] = parseFloat(summary.total_duration_hours.toFixed(2));
  });

  const data = {
    labels: [
      `${t('view.driver-detail.work-performance.Jan')}`, 
      `${t('view.driver-detail.work-performance.Feb')}`, 
      `${t('view.driver-detail.work-performance.Mar')}`, 
      `${t('view.driver-detail.work-performance.Apr')}`, 
      `${t('view.driver-detail.work-performance.May')}`, 
      `${t('view.driver-detail.work-performance.Jun')}`, 
      `${t('view.driver-detail.work-performance.Jul')}`, 
      `${t('view.driver-detail.work-performance.Aug')}`, 
      `${t('view.driver-detail.work-performance.Sep')}`, 
      `${t('view.driver-detail.work-performance.Oct')}`, 
      `${t('view.driver-detail.work-performance.Nov')}`, 
      `${t('view.driver-detail.work-performance.Dec')}`],
    datasets: [
      {
        label: `${t('view.driver-detail.work-performance.working-hrs')}`,
        data: monthlyData,
        // UAT A26-1。クライアント分析と同じ青の濃淡から（config/chartColors）。
        backgroundColor: CHART_SINGLE,
        borderColor: CHART_SINGLE,
        borderWidth: 1,
        borderRadius: 5,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(tickValue: string | number) {
            // EV-337（2026-10-14 PM 判断: 英語は 1 だけ単数）。以前は「1 hrs」。
            return durationText(t, 'ui.duration-h', { hours: Number(tickValue) }, { ns: 'common' });
          },
        },
        grid: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {},
    },
  };

  if (!result || result.key !== requestKey) return <p role="status">{t('ui.loading', { ns: 'common' })}</p>;
  if (result.error) return <FetchFailed error={result.error} onRetry={fetchData} message={t('ui.detail-fetch-failed', { ns: 'common' })} />;
  return <Bar data={data} options={options} />;
};

export default WorkPerformanceGraph;